import { FirebaseApp } from "firebase/app";
import type { Auth, OAuthProvider } from "firebase/auth";
import { ConfigType } from "../../config/ConfigType.ts";
import { Subscribable } from "../Subscribable.ts";
import type { FirebaseAuthAdapter } from "./FirebaseAuthAdapter.ts";
import {
  ProductEvent,
  ProductEventTarget,
} from "../product-events/ProductEventTarget.ts";

export type AuthState = {
  authState: "loading" | "authenticated" | "unauthenticated";
  token?: string;
};

export class AuthService extends Subscribable<AuthState> {
  private readonly _auth: Auth;
  private _microsoftProvider: OAuthProvider | null = null;
  private initialized = false;
  private magicLinkStarted = false;
  private currentUserId: string | null = null;
  constructor(
    private readonly _firebaseApp: FirebaseApp,
    private readonly config: Pick<ConfigType, "microsoft">,
    private readonly localStorage: Pick<
      Storage,
      "setItem" | "getItem" | "removeItem"
    >,
    private readonly authAdapter: FirebaseAuthAdapter,
    private readonly productEvents: ProductEventTarget,
  ) {
    super({
      authState: "loading",
    });
    this._auth = this.authAdapter.getAuth(this._firebaseApp);
  }

  enableEmulator(): void {
    this.authAdapter.connectAuthEmulator(this._auth, "http://127.0.0.1:9099");
  }

  async initialize() {
    if (this.initialized) return;
    this._microsoftProvider =
      this.authAdapter.createOAuthProvider("microsoft.com");
    this._microsoftProvider.setCustomParameters({
      prompt: "consent",
      tenant: this.config.microsoft.clientId,
    });
    this._auth.onAuthStateChanged(async (user) => {
      if (user) {
        const token = await user.getIdToken();
        const newUserId = user.uid;

        // Detect if a different user has logged in (user switch scenario)
        // This can happen when logging in with a different account in another tab
        if (this.currentUserId !== null && this.currentUserId !== newUserId) {
          console.log("Different user detected. Reloading to clear state.", {
            previous: this.currentUserId,
            new: newUserId,
          });
          // Force reload to ensure all services reinitialize with the new user's data
          window.location.reload();
          return;
        }

        this.currentUserId = newUserId;
        this.updateState((draft) => {
          draft.authState = "authenticated";
          draft.token = token;
        });
      } else {
        // User logged out
        this.currentUserId = null;
        this.updateState((draft) => {
          draft.authState = "unauthenticated";
          draft.token = undefined;
        });
      }
    });
    this.initialized = true;
  }

  async refreshToken(): Promise<string | null> {
    try {
      const user = this._auth.currentUser;
      const newToken = await user?.getIdToken(true);
      if (!newToken || !user) {
        this.updateState((draft) => {
          draft.authState = "unauthenticated";
          draft.token = undefined;
        });
        return null;
      }
      this.updateState((draft) => {
        draft.token = newToken;
      });

      return newToken;
    } catch {
      this.updateState((draft) => {
        draft.authState = "unauthenticated";
        draft.token = undefined;
      });
      return null;
    }
  }

  async loginWithMicrosoft() {
    if (!this._microsoftProvider) {
      throw new Error("Microsoft provider is not initialized.");
    }
    try {
      await this.authAdapter.signInWithPopup(
        this._auth,
        this._microsoftProvider,
      );
      this.productEvents.dispatchEvent(new ProductEvent("login"));
    } catch (error: unknown) {
      if (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        (error.code === "auth/popup-closed-by-user" ||
          error.code === "auth/cancelled-popup-request")
      ) {
        // Handle popup closed by user/cancellation silently
        return;
      }
      throw error;
    }
  }

  async submitMagicLink(email: string): Promise<boolean> {
    try {
      await this.authAdapter.sendSignInLinkToEmail(this._auth, email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });
    } catch (error) {
      console.error("Error sending magic link:", error);
      if (typeof error === "object" && error !== null && "code" in error)
        this.productEvents.dispatchEvent(
          new ProductEvent("exception", {
            description: `Error sending magic link: ${error.code}`,
            fatal: false,
          }),
        );
      return false;
    }
    this.localStorage.setItem("magicLinkEmail", email);
    return true;
  }

  async completeMagicLinkLogin(
    link: string,
  ): Promise<{ shouldRedirect: boolean }> {
    if (!this.authAdapter.isSignInWithEmailLink(this._auth, link))
      return { shouldRedirect: false };
    if (this.magicLinkStarted) return { shouldRedirect: true };
    this.magicLinkStarted = true;

    try {
      await this._auth.authStateReady();
      if (this._auth.currentUser) {
        alert("You are already logged in. Please log out first.");
        this.magicLinkStarted = false; // Reset flag to allow retry
        return { shouldRedirect: true };
      }
      let email = this.localStorage.getItem("magicLinkEmail");

      if (!email) {
        email = prompt(
          "You are using a different browser than the one you used to request your login.\n\nFor safety reasons please confirm your email address:",
        );
      }
      if (!email) {
        alert("No email provided for magic link login. Please try again.");
        this.magicLinkStarted = false; // Reset flag to allow retry
        return { shouldRedirect: true };
      }

      await this.authAdapter.signInWithEmailLink(this._auth, email, link);
      this.localStorage.removeItem("magicLinkEmail");
      console.log("Magic link login successful:", email);
      this.productEvents.dispatchEvent(new ProductEvent("login"));

      // Keep flag as true on success - no retry needed
      return { shouldRedirect: true };
    } catch (error) {
      console.error("Error during signInWithEmailLink:", error);
      if (typeof error === "object" && error !== null && "code" in error) {
        alert(
          `There was an error completing your login: ${error.code}. Please contact support.`,
        );
        this.productEvents.dispatchEvent(
          new ProductEvent("exception", {
            description: `Magic link login error: ${error.code}`,
            fatal: false,
          }),
        );
      } else {
        alert(
          "There was an unknown error completing your login. Please contact support.",
        );
      }
      this.magicLinkStarted = false; // Reset flag to allow retry
      return { shouldRedirect: true };
    }
  }

  logout = async () => {
    await this._auth.signOut();
  };
}
